import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleUnit } from './equipmentTypes';
import { useFormsContext } from '../../contexts/FormsContext';
import { formatDateDisplay } from '../../utils/helperFunctions';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import {
    buildUnitReportsPath,
    seedUnitReportFilters,
} from '../../utils/unitReportsUtils';
import {
    fetchLatestDailyServiceReportForUnit,
    resolveDailyServiceReportDisplayDate,
} from '../../utils/reportUtils';
import { getEquipmentReportStatusLabel, seedSelectedUnitForInspect } from './equipmentUnitSnapshotUtils';
import { Btn } from '../ui';

export const EquipmentDailyServiceTab = ({ unit }: { unit: SingleUnit }) => {
    const navigate = useNavigate();
    const { companyDailyServiceForms } = useFormsContext();
    const [lastDailyServiceReport, setLastDailyServiceReport] = useState<any>(null);
    const [dailyServiceSnapshotReady, setDailyServiceSnapshotReady] = useState(false);

    const dailyServiceFormName =
        companyDailyServiceForms.find((f) => f.id === unit.dailyServiceLogFormId)?.name || null;
    const hasDailyServiceForm = !!unit.dailyServiceLogFormId;
    const lastDailyServiceDay =
        resolveDailyServiceReportDisplayDate(lastDailyServiceReport) ?? unit.lastDailyServiceDate ?? null;
    const hasDailyServiceHistory = Boolean(lastDailyServiceDay);

    useEffect(() => {
        let isCancelled = false;
        setDailyServiceSnapshotReady(false);
        setLastDailyServiceReport(null);

        if (!hasDailyServiceForm) {
            setDailyServiceSnapshotReady(true);
            return () => {
                isCancelled = true;
            };
        }

        (async () => {
            try {
                const report = await fetchLatestDailyServiceReportForUnit(unit.id);
                if (!isCancelled && report) {
                    setLastDailyServiceReport(report);
                }
            } catch (err) {
                if (!isCancelled) {
                    console.error('Error fetching last daily service report:', err);
                }
            } finally {
                if (!isCancelled) {
                    setDailyServiceSnapshotReady(true);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [unit.id, hasDailyServiceForm]);

    const handleCreateDailyService = () => {
        if (!unit.categoryId) return;
        seedSelectedUnitForInspect(unit);
        navigate(`/inspect/${unit.categoryId}?unitId=${unit.id}&action=dailyService&returnTo=equipment`);
    };

    const handleViewLogs = () => {
        if (!unit.categoryId) {
            return;
        }
        seedUnitReportFilters(unit.categoryId, unit.id);
        localStorage.setItem('reportsActiveTab', 'daily-service');
        navigate(buildUnitReportsPath(unit.id, { tab: 'daily-service', equipmentCategoryId: unit.categoryId }));
    };

    const dailyServiceStatus = getEquipmentReportStatusLabel(lastDailyServiceReport);

    return (
        <div className="daily-service-tab">
            <div className="snapshots-grid">
                <div className="snapshot-card">
                    <div className="snapshot-header">
                        <h3>Daily Service</h3>
                        {dailyServiceFormName && <span className="snapshot-form-badge">{dailyServiceFormName}</span>}
                    </div>

                    {!hasDailyServiceForm ? (
                        <div className="snapshot-empty-state">
                            <span className="empty-icon">🔧</span>
                            <p>No daily service form assigned.</p>
                            <p className="empty-hint">Assign a daily service form in the unit's Overview tab.</p>
                        </div>
                    ) : !dailyServiceSnapshotReady ? (
                        <div className="snapshot-empty-state">
                            <p>Loading daily service summary…</p>
                        </div>
                    ) : !hasDailyServiceHistory ? (
                        <div className="snapshot-empty-state">
                            <span className="empty-icon">📝</span>
                            <p>No daily service logs yet.</p>
                            <p className="empty-hint">Perform a daily service to view results here.</p>
                        </div>
                    ) : (
                        <div className="snapshot-content">
                            <div className="snapshot-row">
                                <span className="snapshot-label">Last Daily Service</span>
                                <span className="snapshot-value">{formatDateDisplay(lastDailyServiceDay)}</span>
                            </div>
                            {unit.currentUMCounterValue != null && (
                                <div className="snapshot-row">
                                    <span className="snapshot-label">Last {displayUnitOfMeasure(unit.unitOfMeasure) || 'UM'}</span>
                                    <span className="snapshot-value">
                                        {unit.currentUMCounterValue.toLocaleString()}
                                    </span>
                                </div>
                            )}
                            {lastDailyServiceReport?.userName && (
                                <div className="snapshot-row">
                                    <span className="snapshot-label">Performed By</span>
                                    <span className="snapshot-value">{lastDailyServiceReport.userName}</span>
                                </div>
                            )}
                            {dailyServiceStatus && (
                                <div className="snapshot-row">
                                    <span className="snapshot-label">Status</span>
                                    <span className={`snapshot-value ${dailyServiceStatus.className}`}>
                                        {dailyServiceStatus.text}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="snapshot-actions">
                        {hasDailyServiceHistory && (
                            <Btn variant="secondary" buttonText="View Logs" buttonOnClick={handleViewLogs} />
                        )}
                        {hasDailyServiceForm && (
                            <Btn
                                variant="primary"
                                buttonText="Perform Daily Service"
                                buttonOnClick={handleCreateDailyService}
                            />
                        )}
                    </div>
                </div>
            </div>

            {hasDailyServiceHistory && (
                <div className="report-history-link">
                    <Btn
                        variant="link"
                        buttonText="View Complete Log History in Reports Module →"
                        buttonOnClick={handleViewLogs}
                    />
                </div>
            )}
        </div>
    );
};
